import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ReceiptText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCompactCurrency, formatCurrency } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { StackedBar } from '@/components/charts/stacked-bar'
import { FloatingCard, PreviewFrame } from '@/components/features/preview-frame'
import { STATUS_STYLES, merchantInitials } from '@/components/dashboard/transactions-table'

import {
    EXPENSE_CATEGORIES,
    LIVE_FEED,
    TRANSACTIONS
} from '@/constants/dashboard'

const FEED = [...LIVE_FEED, ...TRANSACTIONS]
const VISIBLE = 5

export const ExpensePreview: FC = (): ReactNode => {
    const [ref, inView] = useInView<HTMLDivElement>()
    const reduced = useReducedMotion()
    const [tick, setTick] = useState(0)

    useEffect(() => {
        if (!inView || reduced) return

        const timer = window.setInterval(() => setTick(value => value + 1), 2800)

        return () => window.clearInterval(timer)
    }, [inView, reduced])

    const rows = Array.from({ length: VISIBLE }, (_, index) => {
        const sequence = tick - index
        const item = FEED[((sequence % FEED.length) + FEED.length) % FEED.length]

        return { ...item, sequence }
    })

    return (
        <div ref={ref} className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Live transactions</p>
                        <p className='text-[11px] text-stone'>Synced from 12 connected accounts</p>
                    </div>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-burgundy px-2.5 py-1 text-[11px] font-medium text-cream'>
                        <span className='relative flex size-1.5'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-gold opacity-75 motion-reduce:animate-none' />
                            <span className='relative inline-flex size-1.5 rounded-full bg-gold' />
                        </span>
                        Live
                    </span>
                </div>

                <ul aria-label='Latest transactions' className='mt-4 space-y-1.5'>
                    {rows.map((row, index) => (
                        <li
                            key={`${row.id}-${row.sequence}`}
                            className={cn(
                                'flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors duration-1000',
                                index === 0 && tick > 0 ? 'animate-in fade-in slide-in-from-top-3 bg-gold/15 duration-700' : 'bg-ivory'
                            )}
                        >
                            <span aria-hidden='true' className='flex size-8 shrink-0 items-center justify-center rounded-full bg-cream text-[10px] font-medium'>
                                {merchantInitials(row.merchant)}
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block truncate text-xs font-medium'>{row.merchant}</span>
                                <span className='block truncate text-[11px] text-stone'>{row.category}</span>
                            </span>

                            <span className={cn('whitespace-nowrap text-xs font-medium tabular-nums', row.amount > 0 ? 'text-positive' : 'text-burgundy')}>
                                {row.amount > 0 ? '+' : '−'}
                                {formatCurrency(Math.abs(row.amount), true)}
                            </span>

                            <span className={cn('hidden rounded-full px-2 py-0.5 text-[10px] font-medium sm:inline-flex', STATUS_STYLES[row.status])}>
                                {row.status === 'Review' ? 'Needs review' : row.status}
                            </span>
                        </li>
                    ))}
                </ul>

                <div className='mt-5 border-t border-burgundy/[0.06] pt-4'>
                    <div className='mb-3 flex items-baseline justify-between text-xs'>
                        <span className='text-stone-dark'>Spend this month</span>
                        <span className='font-medium tabular-nums'>$316,920</span>
                    </div>

                    <StackedBar
                        title='Spend this month by category'
                        segments={EXPENSE_CATEGORIES}
                        legendCount={3}
                        formatValue={formatCompactCurrency}
                    />
                </div>
            </PreviewFrame>

            <FloatingCard className='-bottom-8 -right-4 w-60 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <span className='flex size-6 items-center justify-center rounded-full bg-gold/25 text-gold-dark'>
                        <ReceiptText aria-hidden='true' className='size-3.5' />
                    </span>
                    Receipt matched
                </p>

                <p className='mt-1.5 text-[11px] text-stone'>AWS invoice #4471, $12,480.00, from the billing inbox</p>
            </FloatingCard>
        </div>
    )
}
